import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqs, siteInfo } from '@/mocks/comics';
import { OrganizationStructuredData, FAQPageStructuredData, WebPageStructuredData } from '@/components/feature/SEOComponents';
import PageHead from '@/components/base/PageHead';

export default function About() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      setFormSubmitting(false);
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9oojj0senbniolesj00';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus({ type: 'error', message: msg.includes('spam') ? '感谢您的留言！' : msg });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请稍后重试' });
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      <PageHead
        title="关于我们 - 韩国漫画网 | 韩国漫画网站大全"
        description="韩国漫画网成立于2020年，致力于为韩漫爱好者提供韩国漫画网站大全资源，海量免费韩漫在线看，支持下拉式阅读，实时追更热门漫画，日更不断更。了解我们的品牌故事与联系方式。"
        keywords="韩国漫画网, 关于我们, 韩国漫画网站大全, 免费韩漫, 联系方式"
        canonicalUrl="https://www.koreancomic.cn/about"
      />

      <OrganizationStructuredData />
      <WebPageStructuredData
        title="关于我们 - 韩国漫画网"
        description="韩国漫画网成立于2020年，致力于为韩漫爱好者提供韩国漫画网站大全资源。"
        url="https://www.koreancomic.cn/about"
      />
      <FAQPageStructuredData faqs={faqs.slice(0, 4)} />

      <main>
        {/* Page Header */}
        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-2">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              关于 <strong>韩国漫画网</strong>
            </h1>
            <p className="text-foreground-500 text-sm mt-2">韩国漫画网站大全 | 免费韩漫在线看平台</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong>韩国漫画网</strong>成立于{siteInfo.founded}，是国内领先的<strong>韩国漫画网站大全</strong>平台。
                    我们致力于为广大韩漫爱好者提供最全面、最优质的<strong>免费韩漫在线看</strong>服务，
                    汇聚了数千部来自韩国的精品漫画资源。
                  </p>
                  <p>
                    平台支持独创的<strong>下拉式阅读</strong>体验，让读者无需翻页即可沉浸式享受漫画内容。
                    我们的编辑团队每日追踪热门漫画更新，确保<strong>日更不断更</strong>，
                    为读者提供第一时间的<strong>韩漫追更</strong>服务。
                  </p>
                  <p>
                    从恋爱甜宠到热血动作，从奇幻冒险到悬疑推理，
                    韩国漫画网覆盖了所有主流<strong>韩漫大全</strong>分类，
                    满足不同读者的口味需求。我们始终坚持以用户体验为核心，
                    打造最便捷的<strong>漫画在线阅读</strong>平台。
                  </p>
                </div>
              </div>

              {/* Stats Card */}
              <div className="w-full md:w-72 flex-shrink-0">
                <div className="rounded-xl bg-accent-500 p-6 text-white">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold font-heading">2000+</div>
                      <div className="text-xs text-white/70 mt-1">漫画资源</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold font-heading">500万+</div>
                      <div className="text-xs text-white/70 mt-1">月活读者</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold font-heading">12+</div>
                      <div className="text-xs text-white/70 mt-1">分类频道</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold font-heading">日更</div>
                      <div className="text-xs text-white/70 mt-1">更新频率</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features / Reputation */}
        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {siteInfo.features.map((feature, idx) => (
                <div key={idx} className="flex gap-4 p-5 rounded-xl bg-background-50">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className={`${feature.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="px-4 md:px-6 py-12 md:py-16" id="contact">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm mt-2">
                有任何问题或建议，欢迎随时联系我们
              </p>
              <p className="text-sm text-foreground-500 mt-1">
                邮箱：<strong>{siteInfo.email}</strong>
              </p>
            </div>

            <form
              onSubmit={handleContactSubmit}
              data-readdy-form=""
              className="space-y-4"
            >
              {/* Honeypot */}
              <div className="hp-field-wrapper">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="agreement"
                  name="agreement"
                  required
                  className="mt-1 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                />
                <label htmlFor="agreement" className="text-xs text-foreground-500 leading-relaxed">
                  我同意韩国漫画网的隐私政策和服务条款，并了解我的个人信息将按照隐私政策进行处理。
                </label>
              </div>

              {formStatus && (
                <div className={`p-3 rounded-lg text-sm ${
                  formStatus.type === 'success'
                    ? 'bg-accent-100 text-accent-800'
                    : 'bg-primary-100 text-primary-800'
                }`}>
                  {formStatus.message}
                </div>
              )}

              <button
                type="submit"
                disabled={formSubmitting}
                className="w-full py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {formSubmitting ? '提交中...' : '发送留言'}
              </button>
            </form>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              <strong>免费韩漫在线看</strong> 常见问题
            </h2>
            <div className="space-y-3">
              {faqs.slice(0, 4).map((faq, idx) => (
                <div key={idx} className="rounded-xl bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-200/30 transition-colors"
                  >
                    <h3 className="font-heading text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 text-lg flex-shrink-0 ${
                        faqOpen === idx ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {faqOpen === idx && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}